import { AfterViewInit, Component, inject, OnInit, ViewChild } from '@angular/core';
import { Store } from '@ngxs/store';
import { CommonModule } from '@angular/common';
import { MatTableModule } from '@angular/material/table';
import { BehaviorSubject, combineLatest, firstValueFrom, from, Observable, switchMap, tap } from 'rxjs';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDialog } from '@angular/material/dialog';
import { map } from 'rxjs/operators';
import { EntityStateModel, InvalidateLoadedQueries } from '../../core/state/entity.state';
import { makeQueryKey } from '../../utils/entity-states/query-key';
import { ProductEntity } from '../product.entity';
import { ProductEntityService } from '../services/product.entity.service';
import { FormDialogProductComponent } from '../form-dialog-product/form-dialog-product.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FreeFilterInputComponent } from '../../utils/component/free-filter-input/free-filter-input.component';
import { getMinFilterLengthText } from '../../core/util';
import { WidgetMessageService } from '../../core/services/widget-message.service';
import {
  ConfirmDialogRawComponent,
  SimpleConfirmData
} from '../../core/components/confirm-dialog-raw/confirm-dialog-raw.component';
import { LocalStorageService } from '../../core/services/local-storage.service';

@Component({
  selector: 'pixl-main-product',
  templateUrl: './main-product.component.html',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginator,
    MatButton,
    MatIcon,
    MatIconButton,
    MatTooltipModule,
    FreeFilterInputComponent,
  ],
  styleUrls: ['./main-product.component.css']
})

export class MainProductComponent implements OnInit, AfterViewInit {
  private store = inject(Store);
  private widgetService = inject(WidgetMessageService);
  private productService = inject(ProductEntityService);
  private dialog = inject(MatDialog);
  private refresh$ = new BehaviorSubject<void>(undefined);

  products$!: Observable<ProductEntity[]>;
  deletedIds$ = new BehaviorSubject<string[]>([]);

  public readonly freeFilterLen = 3;
  private freeFilter = '';
  private isbnFilter = '';
  private localStorageService = inject(LocalStorageService);

  displayedColumns: string[] = ['actions', 'isbn', 'name', 'updatedAt', 'changedBy'];

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  pageSize = 25;
  pageIndex = 0;
  totalItems = 1000;

  private pageChange$ = new BehaviorSubject<{ pageIndex: number, pageSize: number }>({ pageIndex: this.pageIndex, pageSize: this.pageSize });

  public readonly coloredButtonsEnabled = this.localStorageService.coloredButtonsEnabled;

  async ngOnInit() {
    this.products$ = combineLatest([
      this.pageChange$,
      this.refresh$,
      this.deletedIds$
    ]).pipe(
      switchMap(([{ pageIndex, pageSize }, , deletedIds]) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const query: any = {
          'page[size]': pageSize,
          'page[number]': pageIndex + 1,
          'sort': 'name'
        };

        if (this.freeFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[nameLike]'] = this.freeFilter.trim();
        }

        if (this.isbnFilter?.trim()?.length >= 3) {
          query['page[number]'] = 0;
          query['filter[isbnLike]'] = this.isbnFilter.trim();
        }

        return from(this.productService.ensureLoaded(query)).pipe(
          switchMap(() => this.store.select(this.productService.allByQuery(query))),
          map((products: ProductEntity[]) => products.filter(m => !deletedIds.includes(m.id))),
          tap(() => {
            const state = this.store.selectSnapshot(this.productService.stateClass) as EntityStateModel<ProductEntity>;
            const queryKey = makeQueryKey(query);
            this.totalItems = state.meta?.[queryKey]?.page?.total ?? 0;
          })
        );
      })
    );
  }

  ngAfterViewInit() {
    this.paginator.page.subscribe((event: PageEvent) => {
      this.pageChange$.next({ pageIndex: event.pageIndex, pageSize: event.pageSize });
    });
  }

  add(): void {
    const dialogRef = this.dialog.open(FormDialogProductComponent, {
      minWidth: '40vw',
      maxHeight: '90vh',
      data: {}
    });

    dialogRef.afterClosed().subscribe(async (result: ProductEntity | undefined) => {
      if (!result) return;

      this.store.dispatch(new InvalidateLoadedQueries());
      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Produktdaten erfolgreich erstellt',
        messageTye: 'mtSuccess'
      });
    });
  }

  edit(product: ProductEntity) {
    const dialogRef = this.dialog.open(FormDialogProductComponent, {
      minWidth: '1040px',
      height: '90%',
      data: { ...product }
    });

    dialogRef.afterClosed().subscribe(async (updatedProduct: ProductEntity | undefined) => {
      if (!updatedProduct) return;

      this.refresh$.next();

      this.widgetService.setMessage({
        message: 'Produktdaten erfolgreich editiert',
        messageTye: 'mtSuccess'
      });
    });
  }

  async copy(product: ProductEntity) {
    const copyEntityInstance = Object.assign(new ProductEntity(), product);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (copyEntityInstance as any).id = undefined;
    copyEntityInstance.name = `${copyEntityInstance.name} (Kopie)`;
    copyEntityInstance.isbn = '';
    await this.productService.create(copyEntityInstance);
    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Produktdaten erfolgreich dupliziert. ISBN bitte neu setzen.',
      messageTye: 'mtSuccess'
    });
  }

  async delete(product: ProductEntity) {
    if (!product?.id) {
      return;
    }

    const confirmed = await firstValueFrom(
      this.dialog
        .open<ConfirmDialogRawComponent, SimpleConfirmData, boolean>(ConfirmDialogRawComponent, {
          data: {
            name: product.name ? String(product.name) : product.isbn ? String(product.isbn) : 'Produktdaten',
            headerText: 'Produktdaten löschen'
          }
        })
        .afterClosed()
    );

    if (!confirmed) {
      return;
    }

    await firstValueFrom(this.productService.delete(product.id));

    this.store.dispatch(new InvalidateLoadedQueries());
    this.refresh$.next();

    this.widgetService.setMessage({
      message: 'Produktdaten erfolgreich gelöscht.',
      messageTye: 'mtSuccess'
    });
  }

  public getSearchFilterHint(): string {
    return getMinFilterLengthText(this.freeFilterLen);
  }

  public setFreeFilter(input: string): void {
    this.freeFilter = input;
    this.refresh$.next();
  }

  public setIsbnFilter(input: string): void {
    this.isbnFilter = input;
    this.refresh$.next();
  }

  trackById(index: number, product: ProductEntity): string {
    return product.id;
  }
}
